// Einen Zettel ansehen oder bearbeiten.
//
// Vorher lagen alle Felder auf einmal vor einem — Spalte, Priorität,
// Wiederholung, Checkliste, Timer. Jetzt nur, was man fast immer braucht:
// Art, was, wann, wer. Der Rest liegt unter „Weitere Optionen".
// Schließen speichert — nichts geht verloren.
//
// Zettel vom Betrieb und Fristen aus den Daten lassen sich nicht bearbeiten;
// sie verschwinden von selbst, sobald die Ursache weg ist. Hier gibt es dafür
// die passenden Handgriffe: öffnen, abhaken, zurückstellen, übernehmen.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { Abzeichen, Auswahl, Feld, Knopf } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import type { Aufgabe } from '../../../gemeinsam/lib/typen';
import type { Person } from '../lib/aufgabenEingabe';
import { plus, type Art, type Eintrag } from '../lib/tafelDaten';
import { fuellen, useTafelTexte, type Tafel } from '../lib/useTafel';
import { ART_STIL, ARTEN } from './Zettel';

function dauer(sekunden: number) {
  const s = Math.max(0, Math.floor(sekunden));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

type Entwurf = {
  art: Art; title: string; dueDate: string; uhrzeit: string; bisUhrzeit: string; ort: string;
  assigneeId: string; description: string; priority: Aufgabe['priority'];
  wiederholung: NonNullable<Aufgabe['wiederholung']>; checkliste: NonNullable<Aufgabe['checkliste']>;
};

const ausAufgabe = (a: Aufgabe | null, datum: string, ich: string): Entwurf => ({
  art: a?.art || 'aufgabe', title: a?.title ?? '', dueDate: a ? a.dueDate || '' : datum,
  uhrzeit: a?.uhrzeit || '', bisUhrzeit: a?.bisUhrzeit || '', ort: a?.ort || '',
  assigneeId: a ? a.assigneeId || '' : ich, description: a?.description || '', priority: a?.priority || 'medium',
  wiederholung: a?.wiederholung || '', checkliste: a?.checkliste ?? [],
});

/** Eigener Zettel: bearbeiten oder (ohne `a`) neu anlegen. */
export function ZettelBearbeiten({ a, datum = '', team, ich, mitTimer, tafel, beiSchliessen }: {
  a: Aufgabe | null; datum?: string; team: Person[]; ich: string; mitTimer: boolean; tafel: Tafel; beiSchliessen: () => void;
}) {
  const { t } = useSprache();
  const { heute, tagLang } = useTafelTexte();
  const navigate = useNavigate();
  const [e, setE] = useState<Entwurf>(() => ausAufgabe(a, datum, ich));
  const [punkt, setPunkt] = useState('');
  const [sendet, setSendet] = useState(false);
  const setze = <K extends keyof Entwurf>(k: K, w: Entwurf[K]) => setE(x => ({ ...x, [k]: w }));
  const anfang = ausAufgabe(a, datum, ich);
  const geaendert = JSON.stringify(e) !== JSON.stringify(anfang);
  const fertig = (a?.column || 'todo') === 'done';

  async function speichern(): Promise<void> {
    if (!e.title.trim()) { beiSchliessen(); return; }
    if (a && !geaendert) { beiSchliessen(); return; }
    setSendet(true);
    const daten: Partial<Aufgabe> = {
      ...e, title: e.title.trim(),
      // Nur Termine haben Uhrzeit und Ort.
      uhrzeit: e.art === 'termin' ? e.uhrzeit : '', bisUhrzeit: e.art === 'termin' && e.uhrzeit ? e.bisUhrzeit : '',
      ort: e.art === 'termin' ? e.ort : '',
    };
    const ok = a ? await tafel.aendern(a, daten) : await tafel.anlegen(daten);
    setSendet(false);
    if (ok) beiSchliessen();
  }

  async function umschalten() {
    if (!a) return;
    if (await tafel.aendern(a, { column: fertig ? 'todo' : 'done' })) beiSchliessen();
  }

  function punktDazu() {
    const text = punkt.trim();
    if (!text) return;
    setze('checkliste', [...e.checkliste, { id: `neu_${Date.now().toString(36)}`, text, erledigt: false }]);
    setPunkt('');
  }

  const sekunden = a?.timerRunning && a.timerStartedAt
    ? (a.totalSeconds || 0) + Math.floor((Date.now() - new Date(a.timerStartedAt).getTime()) / 1000)
    : a?.elapsedSeconds ?? 0;
  const tageWahl: Array<[string, string]> = [
    [heute, t('aufgaben.heute')], [plus(heute, 1), t('aufgaben.morgen')], [plus(heute, 7), t('tafel.inEinerWoche')], ['', t('tafel.ablage')],
  ];
  const stil = ART_STIL[e.art];

  return (
    <Fenster beiSchliessen={() => void speichern()} breite="sm:max-w-lg"
             titel={<span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${stil.punkt}`} />
               {a ? t(`tafel.art.${e.art}`) : t('tafel.neuerZettel')}</span>}>
      <div className="space-y-4 p-5">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700" role="radiogroup" aria-label={t('tafel.artWaehlen')}>
          {ARTEN.map(x => (
            <button key={x} type="button" role="radio" aria-checked={e.art === x} onClick={() => setze('art', x)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${e.art === x
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
              <span className={`h-2 w-2 rounded-full ${ART_STIL[x].punkt}`} />{t(`tafel.art.${x}`)}
            </button>
          ))}
        </div>

        <input value={e.title} onChange={ev => setze('title', ev.target.value)} maxLength={500} autoFocus={!a}
               placeholder={t('aufgaben.wasZuTun')} aria-label={t('aufgaben.wasZuTun')}
               onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); void speichern(); } }}
               className="w-full border-0 border-b border-slate-200 bg-transparent px-0 pb-2 text-lg font-semibold text-slate-900 outline-none focus:border-marke-500 dark:border-slate-700 dark:text-slate-100" />

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">{e.art === 'frist' ? t('tafel.bisWann') : t('tafel.wann')}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {tageWahl.map(([d, name]) => (
              <button key={name} type="button" onClick={() => setze('dueDate', d)}
                      className={`rounded-full border px-3 py-1 text-sm transition ${e.dueDate === d
                        ? 'border-marke-600 bg-marke-600 text-white' : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                {name}
              </button>
            ))}
            <input type="date" value={e.dueDate} onChange={ev => setze('dueDate', ev.target.value)} aria-label={t('aufgaben.faellig')}
                   className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
          </div>
          {e.dueDate && !tageWahl.some(([d]) => d === e.dueDate) && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{tagLang(e.dueDate)}</p>
          )}
        </div>

        {e.art === 'termin' && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_1fr_2fr]">
            <Feld label={t('tafel.von')} type="time" value={e.uhrzeit} onChange={ev => setze('uhrzeit', ev.target.value)} />
            <Feld label={t('tafel.bis')} type="time" value={e.bisUhrzeit} disabled={!e.uhrzeit} onChange={ev => setze('bisUhrzeit', ev.target.value)} />
            <div className="col-span-2 sm:col-span-1">
              <Feld label={t('tafel.ort')} value={e.ort} maxLength={200} onChange={ev => setze('ort', ev.target.value)} />
            </div>
          </div>
        )}

        <Auswahl label={t('aufgaben.zustaendig')} value={e.assigneeId} onChange={ev => setze('assigneeId', ev.target.value)}>
          <option value="">{t('aufgaben.niemand')}</option>
          {team.map(p => <option key={p.id} value={p.id}>{p.name || p.username}</option>)}
        </Auswahl>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{t('tafel.notiz')}</span>
          <textarea value={e.description} onChange={ev => setze('description', ev.target.value)} rows={2} maxLength={5000}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-marke-500 focus:outline-none focus:ring-1 focus:ring-marke-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
        </label>

        <details className="group rounded-lg border border-slate-200 dark:border-slate-800"
                 open={e.checkliste.length > 0 || !!e.wiederholung || e.priority !== 'medium' || undefined}>
          <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">{t('tafel.mehr')}</summary>
          <div className="space-y-4 border-t border-slate-200 p-3 dark:border-slate-800">
            <div className="grid gap-3 sm:grid-cols-2">
              <Auswahl label={t('aufgaben.prioritaet')} value={e.priority} onChange={ev => setze('priority', ev.target.value as Aufgabe['priority'])}>
                <option value="low">{t('aufgaben.niedrig')}</option>
                <option value="medium">{t('aufgaben.normal')}</option>
                <option value="high">{t('aufgaben.hoch')}</option>
              </Auswahl>
              <Auswahl label={t('aufgaben.wiederholung')} value={e.wiederholung}
                       onChange={ev => setze('wiederholung', ev.target.value as Entwurf['wiederholung'])}>
                <option value="">{t('aufgaben.keine')}</option>
                <option value="taeglich">{t('aufgaben.taeglich')}</option>
                <option value="woechentlich">{t('aufgaben.woechentlich')}</option>
                <option value="monatlich">{t('aufgaben.monatlich')}</option>
              </Auswahl>
            </div>

            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('aufgaben.checkliste')}
                {e.checkliste.length > 0 && <span className="ms-2 font-normal text-slate-500">{e.checkliste.filter(p => p.erledigt).length}/{e.checkliste.length}</span>}
              </p>
              <ul className="space-y-1">
                {e.checkliste.map(p => (
                  <li key={p.id} className="group/p flex items-center gap-2">
                    <input type="checkbox" checked={p.erledigt}
                           onChange={ev => setze('checkliste', e.checkliste.map(x => x.id === p.id ? { ...x, erledigt: ev.target.checked } : x))} />
                    <span className={`flex-1 text-sm ${p.erledigt ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-200'}`}>{p.text}</span>
                    <button type="button" aria-label={t('aktion.entfernen')} onClick={() => setze('checkliste', e.checkliste.filter(x => x.id !== p.id))}
                            className="rounded p-0.5 text-slate-400 opacity-60 hover:text-red-600 group-hover/p:opacity-100">
                      <Icon name="schliessen" groesse={14} />
                    </button>
                  </li>
                ))}
              </ul>
              {e.checkliste.length < 50 && (
                <div className="mt-1.5 flex items-center gap-2">
                  <input value={punkt} onChange={ev => setPunkt(ev.target.value)} placeholder={t('aufgaben.punktNeu')} maxLength={300}
                         onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); punktDazu(); } }}
                         className="min-w-0 flex-1 rounded-lg border border-dashed border-slate-300 bg-transparent px-2.5 py-1.5 text-sm text-slate-900 focus:border-marke-500 focus:outline-none dark:border-slate-700 dark:text-slate-100" />
                  <Knopf type="button" variante="still" className="!px-2 !py-1" onClick={punktDazu} disabled={!punkt.trim()}>
                    <Icon name="plus" groesse={16} />
                  </Knopf>
                </div>
              )}
            </div>

            {a && mitTimer && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
                <span className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <Icon name="uhr" groesse={16} />{dauer(sekunden)}
                  {a.timerRunning && <Abzeichen art="gruen">{t('aufgaben.laeuft')}</Abzeichen>}
                </span>
                <Knopf type="button" variante={a.timerRunning ? 'sekundaer' : 'still'} className="!py-1 !text-xs" onClick={() => void tafel.timer(a)}>
                  {a.timerRunning ? t('aufgaben.timerStopp') : t('aufgaben.timerStart')}
                </Knopf>
              </div>
            )}
          </div>
        </details>

        {a?.link && <Knopf type="button" onClick={() => navigate(a.link!)}>{t('aufgaben.oeffnen')} →</Knopf>}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
          {a ? (
            <Knopf type="button" variante="still" className="!text-red-600"
                   onClick={async () => { if (window.confirm(t('aufgaben.loeschenFrage')) && await tafel.loeschen(a)) beiSchliessen(); }}>
              {t('aufgaben.loeschen')}
            </Knopf>
          ) : <span />}
          <div className="flex flex-wrap gap-2">
            {a && (
              <Knopf type="button" onClick={() => void umschalten()}>
                <Icon name="haken" groesse={16} />{fertig ? t('tafel.wiederOffen') : t('tafel.erledigt')}
              </Knopf>
            )}
            <Knopf type="button" variante="primaer" laedt={sendet} disabled={!e.title.trim()} onClick={() => void speichern()}>
              {a ? t('aktion.speichern') : t('tafel.anheften')}
            </Knopf>
          </div>
        </div>
      </div>
    </Fenster>
  );
}

/** Zettel vom Betrieb oder Frist aus den Daten: ansehen und handeln. */
export function ZettelAnsehen({ e, tafel, ich, beiSchliessen }: { e: Eintrag; tafel: Tafel; ich: string; beiSchliessen: () => void }) {
  const { t } = useSprache();
  const { zettelTexte, tagLang, heute } = useTafelTexte();
  const { titel, text } = zettelTexte(e);
  const [laeuft, setLaeuft] = useState(false);
  const a = e.auto;
  const link = a?.link ?? e.frist?.link ?? '/';

  async function uebernehmen() {
    const datum = a ? (a.faellig && a.faellig >= heute ? a.faellig : heute) : e.datum;
    const neu = await tafel.anlegen({
      title: titel, description: text, link, quelle: a?.schluessel ?? e.schluessel, art: e.art,
      dueDate: datum, priority: a?.dringlichkeit === 'hoch' ? 'high' : 'medium', assigneeId: ich,
    });
    if (neu) { if (a) tafel.autoWeg(a.schluessel); beiSchliessen(); }
  }

  return (
    <Fenster beiSchliessen={beiSchliessen} breite="sm:max-w-md"
             titel={<span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${ART_STIL[e.art].punkt}`} />{titel}</span>}
             unterzeile={[e.datum ? tagLang(e.datum) : '', e.ueberfaellig ? t('aufgaben.ueberfaellig') : '', t('tafel.automatisch')].filter(Boolean).join(' · ')}>
      <div className="space-y-4 p-5">
        {text && <p className="text-sm text-slate-700 dark:text-slate-300">{text}</p>}
        <p className="text-xs text-slate-500 dark:text-slate-400">{e.quelle === 'frist' ? t('tafel.fristHinweis') : t('aufgaben.vomBetriebHinweis')}</p>
        <div className="flex flex-wrap gap-2">
          {a?.aktion && (
            <Knopf variante="primaer" laedt={laeuft}
                   onClick={async () => { setLaeuft(true); try { await tafel.autoAktion(a); beiSchliessen(); } finally { setLaeuft(false); } }}>
              <Icon name="haken" groesse={16} />
              {t(a.aktion.art === 'lieferschein_geliefert' ? 'aufgaben.aktionGeliefert' : 'aufgaben.aktionBezahlt')}
            </Knopf>
          )}
          <Link to={link} onClick={beiSchliessen}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
            {t('aufgaben.oeffnen')} →
          </Link>
          <Knopf variante="still" onClick={() => void uebernehmen()}><Icon name="plus" groesse={16} />{t('aufgaben.uebernehmen')}</Knopf>
        </div>
        {a && (
          <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-200 pt-3 text-sm dark:border-slate-800">
            <span className="text-slate-500 dark:text-slate-400">{t('aufgaben.zurueckstellen')}:</span>
            {([1, 3, 7] as const).map(n => (
              <Knopf key={n} variante="still" className="!px-2 !py-1 !text-xs"
                     onClick={async () => { await tafel.autoZurueckstellen(a, n); beiSchliessen(); }}>
                {n === 1 ? t('aufgaben.morgen') : fuellen(t('aufgaben.tage'), { n })}
              </Knopf>
            ))}
          </div>
        )}
      </div>
    </Fenster>
  );
}
